/**
 * Save Filter Modal Component
 * Asks user to enable notifications when saving a filter
 */

import React, { useState } from 'react';
import { Bell, BellOff, Heart } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

interface SaveFilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filterName: string;
  onSave: (notifyEnabled: boolean) => void;
  isSaving?: boolean;
}

export const SaveFilterModal: React.FC<SaveFilterModalProps> = ({
  open,
  onOpenChange,
  filterName,
  onSave,
  isSaving = false,
}) => {
  const [notifyEnabled, setNotifyEnabled] = useState(true);

  const handleSave = () => {
    onSave(notifyEnabled);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[340px] rounded-2xl p-6">
        <DialogHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-bg-accent-pink flex items-center justify-center mb-3">
            <Heart className="w-6 h-6 text-text-primary" />
          </div>
          <DialogTitle className="text-lg font-medium text-text-primary">
            Сохранить поиск?
          </DialogTitle>
          <DialogDescription className="text-sm text-text-tertiary mt-2">
            {filterName}
          </DialogDescription>
        </DialogHeader>

        {/* Notification toggle */}
        <div className="flex items-center justify-between py-4 px-3 bg-bg-secondary rounded-xl mt-4">
          <div className="flex items-center gap-3">
            {notifyEnabled ? (
              <Bell className="w-5 h-5 text-text-primary" />
            ) : (
              <BellOff className="w-5 h-5 text-text-tertiary" />
            )}
            <div>
              <p className="text-sm font-medium text-text-primary">
                Уведомлять о новых
              </p>
              <p className="text-xs text-text-tertiary">
                Когда появятся подходящие питомцы
              </p>
            </div>
          </div>
          <Switch
            checked={notifyEnabled}
            onCheckedChange={setNotifyEnabled}
          />
        </div>

        {/* Info text */}
        <p className="text-xs text-text-tertiary text-center mt-3">
          Управлять поисками и уведомлениями можно в разделе{' '}
          <span className="text-text-primary font-medium">Избранное → Поиски</span>
        </p>

        {/* Actions */}
        <div className="flex gap-3 mt-4">
          <Button
            variant="outline"
            className="flex-1 h-11 rounded-full"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Отмена
          </Button>
          <Button
            className="flex-1 h-11 rounded-full bg-text-primary text-white"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Сохраняю...' : 'Сохранить'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaveFilterModal;
